import {
  Box,
  Button,
  Flex,
  Group,
  Modal,
  Text,
  Tooltip,
  rem,
  Loader,
} from "@mantine/core";
import React, { useEffect, useState } from "react";
import { Dropzone } from "@mantine/dropzone";
import { fileStatus, updateFiles, uploadFiles, uploadToTemplate } from "../../services/Document";
import { useSelector } from "react-redux";
import { DatePickerInput } from "@mantine/dates";
import moment from "moment";
import EmptyUploadFile from "../Svgs/EmptyUploadFile";
import DateSvg from "../Svgs/DateSvg";
import useStyles from "./style";
import { FaRegFile } from "react-icons/fa6";
import swal from "sweetalert";
import { getNotifications } from "../../services/notification";
import UploadFileSvg from "../Svgs/UploadFileSvg";
import DocViewer, { DocViewerRenderers } from "@cyntler/react-doc-viewer";
import { uploadFileToS3Api } from "../../utils/PresignedUrlFunction";
import { formatFileSize, showError } from "../../utils";
import "@cyntler/react-doc-viewer/dist/index.css";
import { isStaff } from "../../http";

/**
 * Component to upload files to a selected folder or create a new one.
 *
 * @param {Object} props - Component props
 * @param {Object} props.selectedFolder - selected folder object
 * @param {string} props.userId - user id
 * @param {string} props.userType - user type (staff, client, facility)
 * @param {boolean} props.isPendingAction - whether the action is pending
 * @param {Object} props.uploadedFiles - uploaded files object
 * @param {boolean} props.isnotificationdoc - whether the file is from notification
 */
function UploadStaffDoc({
  selectedFolder,
  userId,
  userType,
  isPendingAction,
  uploadedFiles,
  isnotificationdoc,
  onModalClose,
}) {
  const { classes } = useStyles();
  const [opened, setOpen] = useState(false);
  const { uploadFileLoading } = useSelector((state) => state.document);
  const { updateFileLoading } = useSelector((state) => state.document);
  const [files, setFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [expiryDates, setExpiryDates] = useState([]);
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);
  const [fileSizeError, setFileSizeError] = useState("");
  const [uploadingFiles, setUploadingFiles] = useState(false);
    const { profile } = useSelector((state) => state.profile);

  const close = () => {
    setOpen(false);
    setFiles([]);
    setExpiryDates([]);
    setSelectedFile(null);
    if (onModalClose) onModalClose();
  };
  const open = () => setOpen(true);

  useEffect(() => {
    if (isnotificationdoc) {
      setOpen(true);
    }
  }, [isnotificationdoc]);

  const remove = (id, _index) => {
    setFiles((p) => {
      return p?.filter((v) => v?.name !== id);
    });
    let _tempDates = expiryDates;
    _tempDates = _tempDates?.splice(_index, 1);
    setExpiryDates(_tempDates);
    setSelectedFile(null);
  };

  const unsupportedTypes = [
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ];
  const fileType = selectedFile?.type;

  // Function to upload files to S3 immediately when selected
  const handleFileDrop = async (newFiles) => {
    setUploadingFiles(true);
    setFileSizeError("");
    
    try {
      // Upload new files to S3 immediately
      const uploadedToS3 = await Promise.all(
        newFiles.map(async (file) => {
          const result = await uploadFileToS3Api({ file });
          return {
            name: file.name,
            url: result?.uploadURL,
            size: file.size,
            type: file.type,
          };
        })
      );

      // Update files state with S3 URLs
      const updatedFiles = [...files, ...uploadedToS3];
      setFiles(updatedFiles);
      
      // Set first file as selected if no file is currently selected
      if (!files?.length) {
        setSelectedFile(updatedFiles[0]);
      }
    } catch (error) {
      showError(error?.message || "File upload to S3 failed");
    } finally {
      setUploadingFiles(false);
    }
  };

  const Content = () => {
    return (
      <div style={{ display: "flex", justifyContent: "center" }}>
        <div className={classes.uploadFileContainer} style={{ flex: 1 }}>
          <div className={classes.dropzone}>
            <Dropzone
              onDrop={handleFileDrop}
              onReject={(files) => {
                // Handle rejected files if needed
              }}
              disabled={uploadingFiles}
            >
              <Group
                position="center"
                spacing="xl"
                style={{ minHeight: rem(220), pointerEvents: "none" }}
              >
                <Dropzone.Accept></Dropzone.Accept>
                <Dropzone.Reject></Dropzone.Reject>
                <Dropzone.Idle></Dropzone.Idle>

                <Flex direction={"column"} align={"center"}>
                  {uploadingFiles ? (
                    <>
                      <Loader size="md" />
                      <Text size="md" inline mt={10}>
                        Uploading files...
                      </Text>
                    </>
                  ) : (
                    <>
                      <EmptyUploadFile />
                      <Text size="md" inline>
                        <u>Click to Upload</u> or Drag and Drop
                      </Text>
                    </>
                  )}
                </Flex>
              </Group>
            </Dropzone> 
            {fileSizeError && (
              <Text color="red" size="sm" mt={10}>
                {fileSizeError}
              </Text>
            )}

            {isPendingAction && files?.length > 1 && (
              <Text color="red" size="sm" mt={10}>
                You can only update one file at a time.
              </Text>
            )}
          </div>
          <Flex ml={20} justify={"flex-start"}>
            <Text>File Upload</Text>
          </Flex>
          {!files?.length && uploadedFiles ? (
            <Box className={classes.row}>
              <Box className={classes.column2}>
                <Box className={classes.fileItem}>
                  <Flex align={"center"} gap={5}>
                    <FaRegFile size={15} />
                    <Tooltip label={uploadedFiles?.name}>
                      <Text lineClamp={1} w={180}>
                        {uploadedFiles?.name}
                      </Text>
                    </Tooltip>
                  </Flex>
                  <Text lineClamp={1} w={180} className={classes.fileSize}>
                    {/* {`Size ${value?.size}`} */}
                  </Text>
                </Box>
              </Box>

              <Box className={classes.column2}>
                <Box className={classes.fileExpiryContainer}>
                  <Box className={classes.fileName}>
                    <Text fw={600} ff={"Inter"} fz={14}>
                      Expiry Date:
                    </Text>
                    <Text>
                      {moment(uploadedFiles?.expiry)?.format("MM/DD/YYYY")}
                    </Text>
                  </Box>
                </Box>
              </Box>
            </Box>
          ) : null}
          {files?.length ? (
            <Box className={classes.row} style={{ width: "100%" }}>
              <Box className={classes.column2} style={{ width: "100%" }}>
                {files?.map((value, index) => (
                  <div
                    key={index}
                    style={{
                      width: "100%",
                      display: "flex",
                      flexDirection: selectedFile ? "column" : "row",
                      justifyContent: selectedFile ? "flex-start" : "center",
                      alignItems: selectedFile ? "start" : "center",
                      marginLeft: selectedFile && "10px",
                    }}
                  >
                    <Box
                      className={classes.fileItem}
                      onClick={() => setSelectedFile(value)}
                    >
                      <Flex align={"center"} gap={5}>
                        <FaRegFile size={15} />
                        <Tooltip label={value?.name}>
                          <Text lineClamp={1} w={180}>
                            {value?.name}
                          </Text>
                        </Tooltip>
                      </Flex>
                      <Text lineClamp={1} w={180} className={classes.fileSize}>
                        {`Size ${formatFileSize(value?.size)}`}
                      </Text>
                    </Box>

                    <Box className={classes.dateDeleteContainer}>
                      <Box
                        className={classes.dateContainer}
                        style={{ width: selectedFile && "250px" }}
                      >
                        <DatePickerInput
                          radius={"md"}
                          label="Expiring Date"
                          size="sm"
                          value={
                            expiryDates?.[index]
                              ? new Date(expiryDates?.[index])
                              : null
                          }
                          onChange={(date) => {
                            let temp = [...expiryDates];
                            temp[index] = moment(date).toISOString();
                            setExpiryDates([...temp]);
                          }}
                          placeholder="Pick a date"
                          icon={<DateSvg />}
                          mx="auto"
                          className={classes.datePicker}
                        />
                      </Box>
                      <div
                        style={{ background: "#FEE4E2", borderRadius: "100%" }}
                      >
                        <img
                          src="/assets/images/deleteFolder.png"
                          alt="delete"
                          style={{ width: 30, cursor: "pointer" }}
                          onClick={() => {
                            remove(value?.name, index);
                            expiryDates?.splice(1, index);
                            setExpiryDates([...expiryDates]);
                          }}
                        />
                      </div>
                    </Box>
                  </div>
                ))}
              </Box>
            </Box>
          ) : null}
        </div>

        {(selectedFile || uploadedFiles) && (
          <div
            style={{
              marginRight: "10px",
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              border: "1px solid #E4E4E7",
            }}
          >
            <p style={{ fontWeight: 600 }}>Document Preview</p>
            {unsupportedTypes?.includes(fileType) ? (
              <Flex align={"center"} justifyContent={"center"} gap={5} h={550}>
                This file type is not supported for preview.
              </Flex>
            ) : (
              <DocViewer
                style={{
                  borderTop: "1px solid #E4E4E7",
                  width: "100%",
                  height: 550,
                  overflowY: "auto",
                }}
                config={{ header: { disableHeader: true } }}
                key={1}
                documents={[
                  selectedFile
                    ? {
                        uri: selectedFile.url, // Now using S3 URL instead of createObjectURL
                        fileType: selectedFile.url?.split(".").pop(),
                      }
                    : {
                        uri: uploadedFiles?.fileUrl,
                        fileType: uploadedFiles?.fileUrl?.split(".").pop(),
                      },
                ]}
                pluginRenderers={DocViewerRenderers}
              />
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      {!isnotificationdoc && (
        <Box>
          {isPendingAction ? (
            <Button
              variant="white"
              c="#000"
              ff="Inter"
              fz={12}
              fw={500}
              onClick={open}
            >
              View Details
            </Button>
          ) : (
             !isStaff(profile?.user?.role) && (
            <Button
              leftIcon={<UploadFileSvg size={20} />}
              color="yellow"
              onClick={open}
              style={{ color: "black" }}
            >
              Upload Files
            </Button>
             )
          )}
        </Box>
      )}

      <Modal
        opened={opened}
        shadow="md"
        overlayProps={{ blur: 3 }}
        onClose={close}
        className={classes.uploadModal}
        size={1000}
        withCloseButton={isPendingAction || isnotificationdoc ? true : false}
        zIndex={1000}
        centered
      >
        <Content />
        <Group grow m={10}>
          {isPendingAction ? (
            <Button
              fullWidth
              color="gray"
              variant="light"
              c={"#000"}
              onClick={() => setConfirmClearOpen(true)}
              style={{ width: "50%" }}
            >
              Clear
            </Button>
          ) : isnotificationdoc ? (
            <Button
              fullWidth
              color="gray"
              variant="light"
              c={"#000"}
              onClick={close}
              style={{ width: "50%" }}
            >
              Cancel
            </Button>
          ) : (
            <Button
              fullWidth
              color="gray"
              variant="light"
              c={"#000"}
              onClick={close}
              style={{ width: "50%" }}
            >
              Cancel
            </Button>
          )}
          {uploadedFiles ? (
            <Button
              fullWidth
              disabled={files?.length > 1 || !files?.length}
              loading={updateFileLoading}
              onClick={async () => {
                const fileId = uploadedFiles?._id || uploadedFiles?.id;
                const folderId = selectedFolder?._id || selectedFolder?.id || uploadedFiles?.folder;

                if (uploadedFiles?.isTemplate) {
                  const uploadData = {
                    fileUrl: files[0]?.url,
                    fileName: files[0]?.name,
                    expiry: expiryDates?.[0] || null,
                  };

                  const success = await uploadToTemplate(
                    fileId,
                    uploadData,
                    userId,
                    userType,
                    folderId
                  );

                  if (success) {
                    close();
                    setFiles([]);
                    setExpiryDates([]);
                    await swal({
                      title: "File Uploaded Successfully",
                      text: "Your document has been uploaded to the template",
                      icon: "success",
                      dangerMode: false,
                    });
                  }
                } else {
                  const getUserType = Object.keys(uploadedFiles).includes("staff")
                    ? "staff"
                    : Object.keys(uploadedFiles).includes("client")
                    ? "client"
                    : "facility";

                  const fileMeta = {
                    files: files.map(file => ({
                      name: file.name,
                      url: file.url,
                      size: file.size,
                      type: file.type,
                    })),
                    ...(selectedFolder && { folderId: selectedFolder }),
                    ...(expiryDates?.length && { expiryDates }),
                  };

                  if (userType === "staff") {
                    fileMeta.staff = userId;
                  } else if (userType === "client") {
                    fileMeta.client = userId;
                  } else {
                    fileMeta.facility = userId;
                  }

                  const success = await updateFiles(
                    fileMeta,
                    fileId,
                    userId,
                    getUserType,
                    selectedFolder
                  );

                  if (success) {
                    close();
                    setFiles([]);
                    setExpiryDates([]);
                    getNotifications({
                      page: 1,
                      perPage: 10,
                      client: "",
                    });
                    await swal({
                      title: "File Updated Successfully",
                      text: "Your file has been updated successfully",
                      icon: "success",
                      dangerMode: false,
                    });
                  }
                }
              }}
              color="yellow"
              c="#000"
              style={{ width: "50%" }}
            >
              {uploadedFiles?.isTemplate ? "Upload" : "Update"}
            </Button>
          ) : (
            <Button
              fullWidth
              loading={uploadFileLoading}
              onClick={async () => {
                try {
                  // Files are already uploaded to S3, just prepare the metadata
                  const fileMeta = {
                    files: files.map(file => ({
                      name: file.name,
                      url: file.url,
                      size: file.size,
                      type: file.type,
                    })),
                    ...(selectedFolder?._id && {
                      folderId: selectedFolder._id,
                    }),
                    ...(expiryDates?.length && { expiryDates }),
                  };

                  const payload = {
                    folderId: selectedFolder?._id || selectedFolder?.id,
                    expiryDates,
                    userType,
                    userId,
                    files: fileMeta,
                  };

                  await uploadFiles(
                    payload,
                    selectedFolder?._id || selectedFolder?.id,
                    userId,
                    userType
                  );

                  close();
                  setFiles([]);
                  setExpiryDates([]);
                } catch (error) {
                  showError(error?.message || "File upload failed");
                }
              }}
              color="yellow"
              c="#000"
              style={{ width: "50%" }}
            >
              Save
            </Button>
          )}
        </Group>
      </Modal>
      
      <Modal
        opened={confirmClearOpen}
        onClose={() => setConfirmClearOpen(false)}
        centered
        size="sm"
        shadow="md"
        overlayProps={{ blur: 3 }}
        radius={"md"}
        zIndex={1000}
      >
        <Text ff={"Inter"} fw={400} fz="xl" ta={"center"}>
          You are about to clear this reminder.
        </Text>
        <Text ff={"Inter"} fw={400} fz="md" mb="md" ta={"center"}>
          No action will be taken on the item itself. This action will only
          remove the reminder from your dashboard. Are you sure you want to
          proceed?
        </Text>
        <Group position="center">
          <Button
            variant="default"
            radius={"md"}
            size="sm"
            onClick={() => setConfirmClearOpen(false)}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            color="yellow"
            radius={"md"}
            onClick={() => {
              const getUserType = Object.keys(uploadedFiles).includes("staff")
                ? "staff"
                : Object.keys(uploadedFiles).includes("client")
                ? "client"
                : "facility";
              fileStatus(uploadedFiles?.id, userId, getUserType, {
                deleted: true,
              });
              getNotifications({
                page: 1,
                perPage: 10,
                client: "",
              });
              setConfirmClearOpen(false);
              close();
            }}
          >
            Continue
          </Button>
        </Group>
      </Modal>
    </div>
  );
}

export default UploadStaffDoc;